import { useState, useEffect } from 'react';
import { comprasApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { RefreshCw, Package, FileText, Truck, AlertTriangle, CheckCircle, Clock } from 'lucide-react';

export const ComprasResumen = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await comprasApi.getResumen();
      setData(res.data);
    } catch (err) {
      setError('Error al cargar el resumen');

    } finally {
      setLoading(false);
    }
  };

  const fmt = (num) => num?.toLocaleString('es-CL') || '0';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{error}</p>
        <Button onClick={loadData} className="mt-4">Reintentar</Button>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="compras-resumen-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-primary" />
            Módulo de Compras
          </h1>
          <p className="text-sm text-muted-foreground">
            Resumen de Órdenes de Compra, Pedidos y Recepciones
          </p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Órdenes de Compra */}
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Órdenes de Compra</p>
              <p className="text-2xl font-bold">{fmt(data?.total_oc)}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-500" />
          </div>
          <div className="mt-2 flex gap-4 text-xs">
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle className="w-3 h-3" />
              {fmt(data?.total_oc_aprobadas)} aprobadas
            </span>
            <span className="flex items-center gap-1 text-orange-500">
              <Clock className="w-3 h-3" />
              {fmt(data?.total_oc_pendientes)} pendientes
            </span>
          </div>
        </div>

        {/* Monto Total OC */}
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Monto OC Aprobadas</p>
              <p className="text-2xl font-bold text-green-600">${fmt(data?.monto_total_oc_neto)}</p>
            </div>
            <div className="text-right">
              <span className="text-xs text-muted-foreground">Valor Neto</span>
            </div>
          </div>
        </div>

        {/* Pedidos */}
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Pedidos/Requisiciones</p>
              <p className="text-2xl font-bold">{fmt(data?.total_pedidos)}</p>
            </div>
            <Package className="w-8 h-8 text-purple-500" />
          </div>
          {data?.pedidos_sin_gestionar > 0 && (
            <div className="mt-2">
              <span className="flex items-center gap-1 text-xs text-red-500">
                <AlertTriangle className="w-3 h-3" />
                {fmt(data?.pedidos_sin_gestionar)} items sin OC
              </span>
            </div>
          )}
        </div>

        {/* Recepciones */}
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Recepciones</p>
              <p className="text-2xl font-bold">{fmt(data?.total_recepciones)}</p>
            </div>
            <Truck className="w-8 h-8 text-teal-500" />
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <a href="/compras/ordenes-compra" className="block bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
          <h3 className="font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Órdenes de Compra
          </h3>
          <p className="text-sm text-muted-foreground mt-1">Ver listado de OC</p>
        </a>
        
        <a href="/compras/pedidos" className="block bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
          <h3 className="font-semibold flex items-center gap-2">
            <Package className="w-5 h-5 text-purple-500" />
            Pedidos / Requisiciones
          </h3>
          <p className="text-sm text-muted-foreground mt-1">Ver solicitudes de materiales</p>
        </a>
        
        <a href="/compras/recepciones" className="block bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
          <h3 className="font-semibold flex items-center gap-2">
            <Truck className="w-5 h-5 text-teal-500" />
            Recepciones
          </h3>
          <p className="text-sm text-muted-foreground mt-1">Ver materiales recibidos</p>
        </a>
      </div>

      {/* Reports Links */}
      <div className="mt-6">
        <h2 className="text-lg font-semibold mb-3">Reportes</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a href="/compras/resumen-ots" className="block bg-green-50 border border-green-200 rounded-lg p-4 hover:bg-green-100 transition-colors">
            <h3 className="font-semibold text-green-700">Compras OTs en Ejecución</h3>
            <p className="text-sm text-green-600 mt-1">Costo de materiales por OT con detalle (valores netos)</p>
          </a>
          
          <a href="/compras/reporte-pendientes" className="block bg-orange-50 border border-orange-200 rounded-lg p-4 hover:bg-orange-100 transition-colors">
            <h3 className="font-semibold text-orange-700">OC Pendientes / Pedidos sin Gestionar</h3>
            <p className="text-sm text-orange-600 mt-1">Ver OC sin aprobar y pedidos sin OC</p>
          </a>
        </div>
      </div>
    </div>
  );
};
